import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Zap, Loader2, Users, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  "AI Trade Advisor + persona-based pitches (grounded in live values & news)",
  "DME League Intelligence — VAR re-ranking for your exact scoring, desperation index & manager personas",
  "Pick Futures simulator (10k Monte Carlo off real weekly projections) & Roster Crunch",
  "Command Center action feed & Sell-High Radar",
  "Trade Desk, Trade Finder & Trade Heat Map — buy-low / sell-high targets",
  "Live Draft War Room, Mock Draft simulator & Rookie ADP",
  "Rookie Draft board, Prospect Pipeline, Rookie Scouting & Pick Arbitrage",
  "League Ledger, saved Trade History & live Trade Ticker",
  "My Squad news filtered to your roster + browser push alerts & weekly digests",
  "Multi-league portfolio, exposure alerts & global trade block",
];

const FREE_PERKS = [
  "Multi-asset Dynasty Trade Calculator",
  "League Power Rankings (Starters / Bench / Picks)",
  "Player values & detail pages",
  "Market risers & fallers",
];

export default function Pricing() {
  const { user, access, logout, redeemBeta } = useAuth();
  const [busy, setBusy] = useState(false);
  const [betaCode, setBetaCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [betaStats, setBetaStats] = useState(null);
  const [tw, setTw] = useState(null);
  const [twPosting, setTwPosting] = useState(false);
  const nav = useNavigate();

  const loadTw = () => api.get("/admin/twitter/status").then(r => setTw(r.data)).catch(() => {});
  useEffect(() => { api.get("/admin/beta-stats").then(r => setBetaStats(r.data)).catch(() => {}); loadTw(); }, []);

  const twPostNow = async () => {
    setTwPosting(true);
    try {
      const r = await api.post("/admin/twitter/post-now");
      if (r.data?.ok) toast.success(`Posted to X — tweet ${r.data.tweet_id}`);
      else toast.error(`Not posted: ${r.data?.error || r.data?.skipped || "unknown"}`);
      await loadTw();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setTwPosting(false); }
  };

  const cancel = async () => {
    try { await api.post("/billing/cancel"); toast.success("Cancellation scheduled"); window.location.reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  const resume = async () => {
    try { await api.post("/billing/resume"); toast.success("Subscription resumed"); window.location.reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Resume failed"); }
  };

  const redeem = async () => {
    if (!user) { nav("/login?signup=1"); return; }
    if (!betaCode.trim()) return;
    setRedeeming(true);
    try {
      await redeemBeta(betaCode.trim());
      toast.success("Beta access unlocked — enjoy the full terminal!");
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired beta code");
    } finally { setRedeeming(false); }
  };

  const portalOpen = async () => {
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billing portal unavailable");
    }
  };

  const subscribe = async () => {
    if (!user) { nav("/login?signup=1"); return; }
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", { origin_url: window.location.origin, with_trial: true });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error("Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[var(--bg)]">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-12 fade-up">
          <div className="inline-flex items-center gap-2 border border-[var(--border)] rounded-sm px-3 py-1 text-xs text-[var(--text-2)] mb-6 font-mono">
            <Zap className="w-3 h-3 text-[var(--accent)]" />
            DYNASTY METRICS · PRO ACCESS
          </div>
          <h1 className="font-display text-5xl font-bold" data-testid="pricing-heading">
            {access?.is_subscribed ? "You're on Pro." : "Unlock the full terminal."}
          </h1>
          <p className="text-[var(--text-2)] mt-4 max-w-xl mx-auto">
            Start with a 7-day free trial. Keep the Trade Calculator &amp; Power Rankings free forever. Cancel anytime.
          </p>
        </div>

        <div className="card-bordered p-8 md:p-10 fade-up max-w-md mx-auto" data-testid="pricing-card">
          <div className="flex items-baseline gap-2 mb-1">
            <div className="font-mono text-6xl font-semibold">$9.99</div>
            <div className="text-[var(--text-2)]">/ month</div>
          </div>
          <div className="text-xs tracking-widest text-[var(--text-3)] mb-2">7-DAY FREE TRIAL · THEN $9.99/MO · CANCEL ANYTIME</div>
          <div className="text-[11px] text-[var(--text-2)] mb-6">~$120/yr — less than most league buy-ins. Built to win it back in a single trade or draft.</div>
          <ul className="space-y-3 mb-8">
            {PERKS.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-[var(--accent)] mt-0.5 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <button onClick={subscribe} disabled={busy || access?.is_subscribed} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="subscribe-btn">
            {busy ? <Loader2 className="w-4 h-4 spin" /> : access?.is_subscribed ? "Subscription active" : "Start 7-day free trial"}
          </button>
          {!access?.is_subscribed && (
            <div className="text-center text-[11px] text-[var(--text-3)] mt-2">Card required · auto-charges $9.99/mo when the trial ends</div>
          )}
          {access?.is_subscribed && (
            <button onClick={portalOpen} className="w-full text-xs text-[var(--accent)] hover:underline mt-2" data-testid="portal-btn">
              Manage billing · update card · view invoices
            </button>
          )}
          {access?.is_subscribed && !access?.cancel_at_period_end && (
            <button onClick={cancel} className="w-full text-xs text-[var(--text-3)] hover:text-red-400 mt-3" data-testid="cancel-sub-btn">
              Cancel subscription
            </button>
          )}
          {access?.cancel_at_period_end && (
            <button onClick={resume} className="w-full text-xs text-[var(--accent)] hover:underline mt-3" data-testid="resume-sub-btn">
              Resume subscription
            </button>
          )}
          {access?.on_trial && (
            <div className="text-center text-xs text-[var(--text-3)] mt-4">
              {access.days_left} days left in trial — you can keep using everything for now.
            </div>
          )}
          {access?.is_subscribed && !access?.cancel_at_period_end && (
            <div className="text-center text-xs text-emerald-400 mt-4" data-testid="active-sub-msg">
              Subscription active — auto-renews every 30 days. {access.days_left}d until next renewal.
            </div>
          )}
          {access?.cancel_at_period_end && (
            <div className="text-center text-xs text-amber-400 mt-4" data-testid="cancel-pending-msg">
              Cancellation scheduled. Access ends in {access.days_left}d.
            </div>
          )}
          {user && (
            <div className="text-center mt-4">
              <button onClick={logout} className="text-xs text-[var(--text-3)] hover:underline" data-testid="pricing-logout">Sign out</button>
            </div>
          )}
        </div>

        {/* Free plan summary */}
        {!access?.is_subscribed && (
          <div className="max-w-md mx-auto mt-4 card-bordered p-6 fade-up" data-testid="free-plan-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs tracking-widest text-[var(--text-3)] font-mono">FREE PLAN · NO CARD</div>
              <div className="font-mono text-lg">$0</div>
            </div>
            <ul className="space-y-2 mb-5">
              {FREE_PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-[var(--text-2)]">
                  <Check className="w-4 h-4 text-[var(--text-3)] mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => nav(user ? "/app" : "/login?signup=1")} className="btn-secondary w-full text-sm" data-testid="free-plan-btn">
              {user ? "Continue with Free plan" : "Create a free account"}
            </button>
          </div>
        )}

        {/* Beta code redemption */}
        {!access?.is_subscribed && (
          <div className="max-w-md mx-auto mt-4 card-bordered neon-border p-5 fade-up" data-testid="beta-redeem-card">
            {access?.is_beta ? (
              <div className="text-center text-sm" data-testid="beta-active-msg">
                <span className="text-[var(--accent)] font-mono">BETA ACCESS ACTIVE</span> — full terminal unlocked free. {access.days_left}d remaining.
              </div>
            ) : (
              <>
                <div className="text-xs tracking-widest text-[var(--text-3)] font-mono mb-2">HAVE A BETA CODE?</div>
                <div className="flex gap-2">
                  <input className="input-dark font-mono" placeholder="Enter beta code" value={betaCode} onChange={e => setBetaCode(e.target.value)} data-testid="pricing-beta-input" />
                  <button onClick={redeem} disabled={redeeming} className="btn-secondary whitespace-nowrap" data-testid="pricing-beta-redeem">
                    {redeeming ? <Loader2 className="w-4 h-4 spin" /> : "Redeem"}
                  </button>
                </div>
                <div className="text-[11px] text-[var(--text-3)] mt-2">Beta codes unlock everything free — no card required — until the beta period ends.</div>
              </>
            )}
          </div>
        )}

        {betaStats && (
          <div className="max-w-md mx-auto mt-4 card-bordered p-5" data-testid="beta-stats-card">
            <div className="flex items-center gap-2 text-[10px] tracking-widest text-[var(--accent)] font-mono mb-3">
              <Users className="w-3.5 h-3.5" /> BETA PROGRAM · CODE "{betaStats.code}"
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="font-mono text-2xl text-white">{betaStats.redeemed}</div><div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">REDEEMED</div></div>
              <div><div className="font-mono text-2xl text-[var(--accent)]">{betaStats.converted}</div><div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">CONVERTED</div></div>
              <div><div className="font-mono text-2xl text-[var(--amber)]">{betaStats.days_left}d</div><div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">CODE LEFT</div></div>
            </div>
          </div>
        )}

        {tw && (
          <div className="max-w-md mx-auto mt-4 card-bordered p-5" data-testid="tw-admin-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-[10px] tracking-widest text-[var(--accent)] font-mono">
                <Send className="w-3.5 h-3.5" /> X / TWITTER AUTO-POSTER
              </div>
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${tw.configured ? "text-emerald-500 bg-emerald-500/10" : "text-red-500 bg-red-500/10"}`} data-testid="tw-configured">
                {tw.configured ? "CONFIGURED" : "NOT CONFIGURED"}
              </span>
            </div>
            {tw.last_post?.date && (
              <div className="text-[11px] text-[var(--text-3)] mb-2" data-testid="tw-last-post">
                Last post: <span className="text-[var(--text-2)]">{tw.last_post.date}</span>
                {tw.last_post.tweet_id && <span className="text-[var(--text-3)]"> · id {tw.last_post.tweet_id}</span>}
              </div>
            )}
            {tw.preview && (
              <div className="text-[11px] text-[var(--text-2)] whitespace-pre-wrap rounded-md border border-[var(--border)] bg-white/[0.02] px-3 py-2 mb-3" data-testid="tw-preview">
                {tw.preview}
              </div>
            )}
            <div className="flex items-center gap-2">
              <button onClick={twPostNow} disabled={twPosting || !tw.configured} data-testid="tw-post-now-btn"
                className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
                {twPosting ? <Loader2 className="w-4 h-4 spin" /> : <Send className="w-4 h-4" />} Test post to X now
              </button>
              <button onClick={loadTw} data-testid="tw-refresh-btn" title="Refresh preview & last-post status"
                className="btn-secondary text-sm inline-flex items-center gap-1.5">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>
            <div className="text-[10px] text-[var(--text-3)] mt-2">Force-posts today's tweet immediately (bypasses the once-per-day guard) — use to verify the live X connection.</div>
          </div>
        )}
      </div>
    </div>
  );
}
